import { forwardRef, type ButtonHTMLAttributes, type CSSProperties } from "react";
import { cn } from "@/lib/utils";

type Variant = "primary" | "secondary" | "tint";
type Size = "default" | "cta";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  fullWidth?: boolean;
}

/**
 * Asosiy tugma — Figma §5.1.
 * - default: h37, r12, 12px/500 matn
 * - cta:     to'liq kenglik, h56, r16, 16px/600 matn (sticky pastki panel uchun)
 * Ranglar `var(--primary)` / `var(--c-line)` orqali — Click/Payme/Zoomrad override qiladi.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "default",
    loading = false,
    fullWidth = false,
    disabled,
    className,
    children,
    ...rest
  },
  ref,
) {
  const isCta = size === "cta";
  const isDisabled = disabled || loading;

  const style: CSSProperties =
    variant === "primary"
      ? {
          background: isDisabled
            ? "color-mix(in srgb, var(--primary) 55%, var(--c-surface))"
            : "var(--primary)",
          color: "var(--c-on-brand)",
        }
      : variant === "tint"
        ? {
            background: "var(--c-brand-tint)",
            color: "var(--primary)",
          }
        : {
            // --c-line — stroke tokeni, fill uchun emas (Click/Payme/Zoomrad'da
            // qattiqroq bo'lib ko'rinishi mumkin) — shuning uchun --c-elevated-2.
            background: "var(--c-elevated-2)",
            color: isDisabled ? "var(--c-text-disabled)" : "var(--c-text)",
          };

  return (
    <button
      ref={ref}
      disabled={isDisabled}
      style={style}
      className={cn(
        "inline-flex items-center justify-center gap-2.5 font-semibold transition-all",
        "active:scale-[0.98] disabled:cursor-not-allowed",
        isCta
          ? "h-14 rounded-2xl px-5 text-base"
          : "h-[37px] rounded-xl px-3 text-xs tracking-[0.2px]",
        fullWidth && "w-full",
        loading && "opacity-80",
        className,
      )}
      {...rest}
    >
      {loading && (
        <span
          className="size-5 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden
        />
      )}
      {children}
    </button>
  );
});
